import { useQuery as useApolloQuery } from '@apollo/client';
import { restApiService, RestApiServiceParams, JsonRestApiRequestConfig } from '@tableau/vizportalclient-common-components';
import Axios from 'axios';
import { GetUserQuery, GetUserDocument } from '../../../../../../gen-src/queries';
import { TableauUser } from '../../UserDisplayComponent';

export type GetUserResponseWrapper = {
  readonly user: GetUserResponse;
};

type GetUserResponse = {
  readonly id: string;
  readonly fullName?: string;
  readonly username: string;
  readonly name?: string;
  readonly domain: {
    readonly name: string;
  }
}

function getUsersRequestConfig(params: UserParams): JsonRestApiRequestConfig {
  return {
    restApiVersion: '3.16',
    serverWideRequest: false,
    endpoint: `users/${params.userLuid}`,
    httpMethod: 'GET'
  }
}

export type UserParams = {
  readonly userLuid: string;
  readonly makeJsonRestApiRequest: ReturnType<typeof restApiService>['makeJsonRestApiRequest'];
  readonly siteLuid?: string;
};

function queryUserOnSite(params: UserParams): Promise<GetUserResponseWrapper> {
  Axios.defaults.baseURL = window.location.origin;
  const makeJsonRestApiRequest = params.makeJsonRestApiRequest;
  const result = makeJsonRestApiRequest<string, GetUserResponseWrapper>(getUsersRequestConfig(params));
  return result;
}

export async function getRestUsers(luids: ReadonlyArray<string>, params: RestApiServiceParams): Promise<Map<string, TableauUser>> {
  const { makeJsonRestApiRequest } = restApiService(params);

  const result = await Promise.all(luids.map(t => queryUserOnSite({ userLuid: t, makeJsonRestApiRequest })));
  const users = new Map(result.map(i => [i.user.id, { username: i.user.name ?? null, name: i.user.fullName ?? null, domain: i.user.domain.name }]));
  return users;
}

export function useGetGraphUsers(luids: ReadonlyArray<string>) {
  const {loading, data} = useApolloQuery<GetUserQuery>(GetUserDocument, {
    variables: { luids: luids },
    skip: luids.length === 0,
  });

  return {data, loading};
}

export function useGetUsers(luids: ReadonlyArray<string>) {
  const { data: graphUsers, loading: graphUsersLoading } = useGetGraphUsers(luids);

  const authors: Map<string, TableauUser> = new Map<string, TableauUser>();
  graphUsers?.items.forEach(val => {
    authors.set(val.luid, { name: val.name, domain: val.domain, username: val.username })
  });

  return { authors, loading: graphUsersLoading };
}
